import React, {FC, useCallback, useEffect, useState} from 'react'
import {ToastContainer} from 'react-toastify'
import {PageTitle} from '../../../../_metronic/layout/core'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {apiErrorMessage} from '../../public/PublicModels'
import {fullName, TRACKING_STATUS_OPTIONS} from '../application/ApplicationModel'
import {emptyFilters, IListFilters, SearchFilterBar} from '../shared/SearchFilterBar'
import {useJobProcessOptions} from '../shared/useJobProcessOptions'
import {advanceTracking, backTracking, deleteTracking, searchTrackings, updateTracking} from './Api'
import {ITrackingModel, ITrackingUpdateRequest} from './TrackingModel'
import {TrackingTable} from './TrackingTable'
import {NextStepModal} from './NextStepModal'
import {TrackingDetailModal} from './TrackingDetailModal'
import {TrackingEditModal} from './TrackingEditModal'
import {ITagTarget, removeCandidateTag, TagAssignModal} from '../tag/TagAssignModal'
import {useTagOptions} from '../tag/useTagOptions'
import {ITagModel} from '../tag/TagModel'
import {CreateInterviewModal, IInterviewTarget} from '../interview/CreateInterviewModal'

/** Aday takibi: adayları süreç adımları arasında ilerletme / geri alma */
const TrackingPage: FC = () => {
  const {jobs, processes} = useJobProcessOptions()
  const [filters, setFilters] = useState<IListFilters>(emptyFilters)
  const [trackings, setTrackings] = useState<ITrackingModel[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [branchingItem, setBranchingItem] = useState<ITrackingModel | null>(null)
  const [detailId, setDetailId] = useState<string | null>(null)
  const [editingItem, setEditingItem] = useState<ITrackingModel | null>(null)
  const [deletingItem, setDeletingItem] = useState<ITrackingModel | null>(null)
  const [tagTarget, setTagTarget] = useState<ITagTarget | null>(null)
  const [interviewTarget, setInterviewTarget] = useState<IInterviewTarget | null>(null)
  const {tags, reloadTags} = useTagOptions()

  const fetchTrackings = useCallback(async (page: number, activeFilters: IListFilters) => {
    setLoading(true)
    try {
      const payload = (await searchTrackings(activeFilters, page)).data.payload
      setTrackings(payload.content)
      setTotalPages(payload.totalPages)
      setCurrentPage(page)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Aday takip kayıtları yüklenemedi'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTrackings(0, filters)
  }, [filters, fetchTrackings])

  const handleSearch = useCallback((q: string) => setFilters((current) => ({...current, q})), [])
  const handleFilter = useCallback(
    (selection: Omit<IListFilters, 'q'>) => setFilters((current) => ({...current, ...selection})),
    []
  )

  /** Satırı yeniden yüklemeden sunucunun döndürdüğü güncel kayıtla değiştirir */
  const replaceRow = (updated: ITrackingModel) =>
    setTrackings((rows) => rows.map((row) => (row.id === updated.id ? updated : row)))

  const runStepAction = async (tracking: ITrackingModel, action: () => Promise<{data: {payload: ITrackingModel}}>, message: string) => {
    setBusyId(tracking.id)
    try {
      const {data} = await action()
      replaceRow(data.payload)
      showSuccessToast(`${fullName(tracking)}: ${message} → ${data.payload.currentStepName}`)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Adım değiştirilemedi'))
    } finally {
      setBusyId(null)
    }
  }

  const handleAdvance = (tracking: ITrackingModel) => {
    if (tracking.nextSteps.length > 1) {
      setBranchingItem(tracking)
      return
    }
    runStepAction(tracking, () => advanceTracking(tracking.id), 'ilerletildi')
  }

  const handleBranchSelect = (nextStepId: string) => {
    const tracking = branchingItem
    setBranchingItem(null)
    if (tracking) runStepAction(tracking, () => advanceTracking(tracking.id, nextStepId), 'ilerletildi')
  }

  const handleBack = (tracking: ITrackingModel) =>
    runStepAction(tracking, () => backTracking(tracking.id), 'geri alındı')

  const toTagTarget = (tracking: ITrackingModel): ITagTarget | null =>
    tracking.candidateId
      ? {candidateId: tracking.candidateId, candidateName: fullName(tracking), tags: tracking.tags || []}
      : null

  const handleRemoveTag = async (tracking: ITrackingModel, tag: ITagModel) => {
    const target = toTagTarget(tracking)
    if (!target) return
    try {
      await removeCandidateTag(target, tag)
      await fetchTrackings(currentPage, filters)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Etiket kaldırılamadı'))
    }
  }

  const handleUpdate = async (request: ITrackingUpdateRequest) => {
    if (!editingItem) return
    setSaving(true)
    try {
      const {data} = await updateTracking(editingItem.id, request)
      replaceRow(data.payload)
      showSuccessToast('Aday takibi güncellendi')
      setEditingItem(null)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Güncellenemedi'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deletingItem) return
    setLoading(true)
    try {
      await deleteTracking(deletingItem.id)
      showSuccessToast('Aday takip kaydı silindi')
      await fetchTrackings(currentPage, filters)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Silinemedi'))
      setLoading(false)
    } finally {
      setDeletingItem(null)
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Aday Takip</PageTitle>

      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Aday Takip</span>
            <span className='text-muted mt-1 fw-bold fs-7'>
              Adayları işe alım sürecinin adımları arasında İlerle / Geri ile taşıyın
            </span>
          </h3>
        </div>
        <div className='card-body pt-0'>
          <SearchFilterBar
            jobs={jobs}
            processes={processes}
            statusOptions={TRACKING_STATUS_OPTIONS}
            tags={tags}
            onSearch={handleSearch}
            onFilter={handleFilter}
          />
          <TrackingTable
            data={trackings}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => page >= 0 && page < totalPages && fetchTrackings(page, filters)}
            onBack={handleBack}
            onAdvance={handleAdvance}
            onDetail={(tracking) => setDetailId(tracking.id)}
            onEdit={setEditingItem}
            onDelete={setDeletingItem}
            onTag={(tracking) => setTagTarget(toTagTarget(tracking))}
            onInterview={(tracking) =>
              setInterviewTarget({
                trackingId: tracking.id,
                candidateName: fullName(tracking),
                candidateEmail: tracking.email,
                jobTitle: tracking.jobTitle,
              })
            }
            onRemoveTag={handleRemoveTag}
            busyId={busyId}
            loading={loading}
          />
        </div>
      </div>

      <NextStepModal tracking={branchingItem} onClose={() => setBranchingItem(null)} onSelect={handleBranchSelect} />
      <TrackingDetailModal trackingId={detailId} onClose={() => setDetailId(null)} />
      <CreateInterviewModal target={interviewTarget} onClose={() => setInterviewTarget(null)} />
      <TagAssignModal
        target={tagTarget}
        onClose={() => setTagTarget(null)}
        onSaved={() => {
          reloadTags()
          fetchTrackings(currentPage, filters)
        }}
      />
      <TrackingEditModal
        tracking={editingItem}
        loading={saving}
        onClose={() => setEditingItem(null)}
        onSubmit={handleUpdate}
      />

      <ConfirmModal
        show={!!deletingItem}
        header='Silme Onayı'
        text={`${deletingItem ? fullName(deletingItem) : ''} adayının takip kaydı ve süreç geçmişi silinecek. Başvuru ve aday kaydı korunur; süreç Gelen Başvurular ekranından yeniden başlatılabilir.`}
        handleClose={() => setDeletingItem(null)}
        applyOnClick={handleDelete}
      />

      <ToastContainer position='top-right' autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
    </>
  )
}

export {TrackingPage}
